import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { faPercent } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { AffiliatesApiAdapter } from '../../adapters/AffiliatesApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';

export interface CreateCodeModalRef {
  open: (onConfirm: (name: string) => void) => void;
}

export const CreateCodeModal = forwardRef<CreateCodeModalRef, {}>((props, ref) => {
  const { t } = useTranslation('affiliatesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<(name: string) => void>(() => {});
  const [name, setName] = useState('');
  const { toast } = useToast();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AffiliatesApiAdapter(httpClient);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    open: (onConfirm: (name: string) => void) => {
      confirmRef.current = onConfirm;
      setName('');
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (!name) return;
    setLoading(true);
    try {
      await apiAdapter.createAffiliateCode(name);
      toast({
        title: t('createCodeModal.success'),
      });
      confirmRef.current(name);
      confirmRef.current = () => {};
      modalRef.current?.closeModal();
    } catch (error: any) {
      toast({
        title: t('createCodeModal.error'),
        description: error?.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('createCodeModal.title')} type="default" width="default">
      <div className="space-y-4">
        <div>
          <label className="block text-sm mb-1 font-medium">
            {t('createCodeModal.nameLabel')} <span className="text-danger">*</span>
          </label>
          <Input
            type="text"
            placeholder={t('createCodeModal.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full"
            autoFocus
          />
        </div>
        <Alert className="flex items-center gap-4">
          <FontAwesomeIcon icon={faPercent} className="text-primary text-xl" />
          <div className="text-left">
            <span className="font-semibold">{t('createCodeModal.infoTitle')}</span>
            <span> {t('createCodeModal.infoDesc')}</span>
          </div>
        </Alert>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('createCodeModal.cancel')}
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm} disabled={!name || loading}>
            {t('createCodeModal.create')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateCodeModal.displayName = 'CreateCodeModal';
